import { useMutation, useQueryClient } from '@tanstack/react-query'
import { postRequest, putRequest } from '../api'
import { displayErrorToast } from '../helpers'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

interface UpdateEntityOptions<T> {
  entityType: string
  id?: number
  queryKey?: string
  queryKeyValue?: string | number
  parentEntityType?: string
  parentId?: number
  stringifyValues?: (keyof T)[]
  onSuccess?: (data: RecursivePartial<T>) => void
}

export const useUpdateTeam = <T extends wowaudit.Team>(team: wowaudit.Team) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RecursivePartial<T>) => {
      return putRequest(`/api/teams/${team.id}`, { team: data })
    },
    onSuccess: (data: RecursivePartial<T>) => {
      queryClient.setQueryData(['team', { path: team.path }], data)
      toast.success(t('shared.changes-saved'))
    },
    onError: (error) => displayErrorToast(error, t),
  })
}

export const useUpdateEntity = <T>({
  entityType,
  id,
  queryKey = 'id',
  queryKeyValue = id,
  parentEntityType,
  parentId,
  stringifyValues = [],
  onSuccess,
}: UpdateEntityOptions<T>) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const baseUrl = parentEntityType
    ? `/api/${parentEntityType}s/${parentId}/${entityType}s`
    : `/api/${entityType}s`

  return useMutation({
    mutationFn: (data: RecursivePartial<T>) => {
      const transformedData: Record<string, unknown> = {}
      Object.entries(data).forEach(([key, value]) => {
        if (stringifyValues.includes(key as keyof T)) {
          transformedData[key] = JSON.stringify(value)
        } else {
          transformedData[key] = value
        }
      })

      return id
        ? putRequest(`${baseUrl}/${id}`, { [entityType]: transformedData })
        : postRequest(`${baseUrl}`, { [entityType]: transformedData })
    },
    onSuccess: (data: RecursivePartial<T>) => {
      queryClient.setQueryData(
        [entityType, { [queryKey]: queryKeyValue }],
        data,
      )
      toast.success(t('shared.changes-saved'))
      onSuccess?.(data)
    },
    onError: (error) => displayErrorToast(error, t),
  })
}
